"use client";

import { BlurFade } from "@/components/ui/blur-fade";
import { Typography } from "@/components/ui/typography";
import { useCommonTranslation } from "@/features/i18n/libs/useCommonTranslation";
import { RichTrans } from "@/features/i18n/ui/rich-trans";
import { ComprehensiveDiagnosisSection } from "@/features/landing/ui/comprehensive-diagnosis-section";
import { ReportExampleButton } from "@/features/landing/ui/report-example-button";
import { StartButton } from "@/features/landing/ui/start-button";
import { useEffect, useRef } from "react";

// Create a simple store for tracking hero button visibility
const heroButtonVisibility = {
  isVisible: true,
  listeners: new Set<() => void>(),
  timeoutId: null as NodeJS.Timeout | null,

  setVisible(visible: boolean) {
    if (this.isVisible === visible) return;

    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }

    this.timeoutId = setTimeout(() => {
      this.isVisible = visible;
      this.listeners.forEach((listener) => listener());
      this.timeoutId = null;
    }, 50);
  },

  subscribe(listener: () => void) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  },
};

export { heroButtonVisibility };

export function HeroTextSection() {
  const { t } = useCommonTranslation();
  const startButtonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = startButtonRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry) {
          heroButtonVisibility.setVisible(entry.isIntersecting);
        }
      },
      {
        threshold: [0, 0.5],
        rootMargin: "0px 0px -100px 0px",
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div className="max-w-lg flex-1 items-end lg:flex lg:max-w-[700px]">
      <div className="flex flex-col gap-6">
        <BlurFade delay={0.5}>
          <Typography
            variant="h1"
            className="!text-3xl leading-tight lg:!text-[40px]"
          >
            <RichTrans i18nKey="landing:hero_title" />
          </Typography>
        </BlurFade>

        <BlurFade delay={1}>
          <div className="">
            <Typography
              className="text-l mr-3 text-gray-500 line-through"
              data-nosnippet
            >
              {t("landing:price")}
            </Typography>
            <Typography className="text-xl font-bold text-violet">
              {t("landing:free_for_limited_time")}
            </Typography>
          </div>
        </BlurFade>

        <div className="flex flex-col gap-4" ref={startButtonRef}>
          <BlurFade delay={1.5}>
            <StartButton />
          </BlurFade>
          <BlurFade delay={3}>
            <ReportExampleButton variant="short" />
          </BlurFade>
        </div>
      </div>

      <div className="absolute bottom-[40px] left-1/2 z-10 hidden -translate-x-1/2 lg:block">
        <ComprehensiveDiagnosisSection />
      </div>
    </div>
  );
}
